import {Injectable} from '@angular/core';
import {ItemListFilter, ItemBulkEditModel, ItemInItemGroupModel, WithSpecsModel} from 'web-shared';
import {map, shareReplay} from 'rxjs/operators';
import {ItemsQuery} from 'web-shared';
import {
  FilterModel,
  GLOBAL,
  IdCodeNameQuery,
  MesApiService,
  Page,
  PagedData,
  stringCompare
} from 'common-shared';
import {combineLatest, Observable} from "rxjs";
import {instanceToInstance, plainToInstance} from "class-transformer";
import {ItemListQuery} from "web-shared";
import {SpecLineModel} from "web-shared";
import {
  ManufacturingItemGroupService
} from "../../manufacturing/manufactured-item-groups/manufacturing-item-group.service";
import {FilterMetadata} from "primeng/api";

@Injectable({
  providedIn: 'root'
})
export class ItemService extends MesApiService {
  constructor(public query: ItemsQuery, private itemGroupService: ManufacturingItemGroupService) {
    super('Item');
  }

  getAll$ = this.query.getAll$.pipe(shareReplay(1));
  get$ = (id: string) => this.query.selectEntity(id).pipe(shareReplay(1));

  getItemsCombinedToGroups$: Observable<IdCodeNameQuery[]> = combineLatest([this.query.getAll$, this.itemGroupService.query.selectAll()])
    .pipe(map(([items, groups]) => ({items, groups})))
    .pipe(map(value => {
      let items: IdCodeNameQuery[] =
        (value.items.filter(item => item.StatusClv !== GLOBAL.Classificators.ItemStatus.Draft) || []).map(value1 => value1 as IdCodeNameQuery)
          .concat((value.groups.filter(group => group.StatusClv !== GLOBAL.Classificators.ItemGroupStatus.Draft) || []).map(value1 => value1 as IdCodeNameQuery));
      return items.sort((a, b) => stringCompare(a, b, obj => obj.DisplayName));
    }), shareReplay(1));

  getItemsWithAlternativeGroups$: Observable<IdCodeNameQuery[]> = combineLatest([this.query.selectAll(), this.itemGroupService.query.selectAll()])
    .pipe(map(([items, groups]) => {
      groups = groups.filter(group => group.TypeClv === GLOBAL.Classificators.ItemGroupType.Alternative && group.StatusClv !== GLOBAL.Classificators.ItemGroupStatus.Draft);
      let itemsWithAlternatives = groups?.map(group => group as IdCodeNameQuery).concat(items?.map(item => item as IdCodeNameQuery));
      return itemsWithAlternatives.sort((a, b) => stringCompare(a, b, obj => obj.DisplayName));
    }), shareReplay(1));

  getNotDrafts$ = this.query.getAll$.pipe(map(items => {
    return (items.filter(item => item.StatusClv !== GLOBAL.Classificators.ItemStatus.Draft) || [])
      .sort((a, b) => stringCompare(a, b, obj => obj.DisplayName));
  }), shareReplay(1));

  getByFilter(filter: FilterModel<ItemListFilter>) {
    for (let specValues of Object.values(filter.Data.Specs ?? {})) {
      for (let specValue of specValues as Array<FilterMetadata>) {
        if(specValue.matchMode == 'equals' && specValue.value == null){
          specValue.value = '*';
        }
      }
    }

    let clonedFilter = instanceToInstance(filter);
    return this.addGet<FilterModel<ItemListFilter>, PagedData<ItemListQuery>>(clonedFilter, {action: 'GetByFilter'}).pipe(map(value1 => {
      return new PagedData<ItemListQuery>(plainToInstance(ItemListQuery, value1.data.Data), plainToInstance(Page, value1.data.Page), value1.data.DistinctSpecs);
    }));
  }

  getSpecs(id: string): Observable<SpecLineModel[]> {
    return this.getList<SpecLineModel>({action: 'GetSpecs', id: id}).pipe(map(value1 => {
      return plainToInstance(SpecLineModel, value1.data);
    }));
  }

  getSpec(itemId: string, propertyId: string) {
    return this.getSingle<SpecLineModel>({action: 'GetSpec'}, {id: itemId, propertyId: propertyId}).pipe(map(value => {
      return plainToInstance(SpecLineModel, value.data);
    }));
  }

  getWithSpecs(itemIds: string[], propertyIds: string[]) {
    return this.addGet<any, WithSpecsModel[]>({
      itemIds: itemIds.filter(id => id),
      propertyIds: propertyIds.filter(id => id)
    }, {action: 'GetWithSpecs'}).pipe(map(value => {
      return plainToInstance(WithSpecsModel, value.data);
    }));
  }

  applyBulkEdit(filter: FilterModel<ItemListFilter>, editRules: SpecLineModel[]) {
    let clonedFilter = instanceToInstance(filter);
    clonedFilter.Page.Size = 0;
    clonedFilter.Page.Number = 0;
    const itemBulkEditModel = new ItemBulkEditModel(clonedFilter, editRules);
    return this.addGet<ItemBulkEditModel, number>(itemBulkEditModel, {action: 'BulkEdit'}).pipe(map(value => {
      return value.data;
    }));
  }

  createItemInItemGroup(itemInItemGroup: ItemInItemGroupModel) {
    return this.addGet<ItemInItemGroupModel, ItemInItemGroupModel>(itemInItemGroup, {action: 'CreateItemInItemGroup'}).pipe(map(value => {
      return plainToInstance(ItemInItemGroupModel, value.data);
    }));
  }

  editItemInItemGroup(itemInItemGroup: ItemInItemGroupModel) {
    return this.update<ItemInItemGroupModel>(itemInItemGroup.Id, itemInItemGroup, {action: 'EditItemInItemGroup'}).pipe(map(value => {
      return plainToInstance(ItemInItemGroupModel, value.data);
    }));
  }

  removeItemInItemGroup(itemInItemGroup: ItemInItemGroupModel) {
    return this.delete<string>(itemInItemGroup.Id, {action: 'RemoveItemInItemGroup'}).pipe(map(value => {
      return value.data;
    }));
  }

  saveAsTemplate(itemId: string, itemGroupId: string) {
    return this.getSingle<SpecLineModel>({action: 'SaveAsTemplate'}, {id: itemId, itemGroupId: itemGroupId}).pipe(map(value => {
      return plainToInstance(SpecLineModel, value.data);
    }));
  }
}
